import { createContext } from '@lit/context'
import type { AlignItems, CombinedLayoutConfig, ComponentWidgetConfig, DynamicSectionCfg, FlexDirection, JustifyContent, ProductImageConfig, WidgetConditionCfg, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'
import type { AreaLayout, ConfigMode, MobileConfigMode, Section, WidgetCopyOptions } from './editor-state.js'

export type CanvasStateContextType = Record<ConfigMode, Section[]>
export type AreaLayoutContextType = Record<ConfigMode, AreaLayout>
export type ImageConfigContextType = ProductImageConfig

// The productInfo/orderActions area-level settings authored via the sidebar's "Area: ..."
// panel — padding (shared with the image area, see AreaPaddingCfg) and row/column gap
// (productInfo/orderActions only, no image equivalent — see DynamicSectionCfg). Despite the
// name, this now carries both; kept as a single Pick off DynamicSectionCfg's own fields so
// the two stay in sync with the exported tile-config shape by construction, not by hand.
export type DynamicAreaSettings = Pick<DynamicSectionCfg, 'paddingTop' | 'paddingRight' | 'paddingBottom' | 'paddingLeft' | 'rowGap' | 'columnGap' | 'verticalAlign'>

// 'above'/'below' — see ImageFlowZoneCfg. Always exactly one zone each, so unlike
// productInfo/orderActions there's no row/column layout for them to have a row/column gap
// *between* — their own DynamicAreaSettings entries below only ever carry padding; rowGap/
// columnGap exist on the type (inherited) but are simply never populated for these two keys.
export type ImageFlowSlot = 'above' | 'below'

export interface DynamicAreaPadding {
  productInfo?: DynamicAreaSettings
  orderActions?: DynamicAreaSettings
  above?: DynamicAreaSettings
  below?: DynamicAreaSettings
}

export type AreaPaddingContextType = Record<ConfigMode, DynamicAreaPadding>

export interface SelectedZoneRef {
  mode: ConfigMode
  sectionId: string
  widgetId: string | null
}

export type SelectedWidgetContextType = SelectedZoneRef | null
export type ImageFocusContextType = ConfigMode | null

// Mobile preview is view-only — it re-renders the existing grid/list config the way the real
// csc-base-product-tile would on mobile (card-variant "inline" instead of "wide" for list; grid
// is unchanged, see csc-plp-grid's getEffectiveCardVariant) without any separate mobile branch
// in tile-config itself. Global (not per-mode) since it's one toggle for both canvases at once.
export type MobileModeContextType = boolean

export type CustomizedMobileModesContextType = MobileConfigMode[]

export type DynamicArea = 'productInfo' | 'orderActions'

// Selecting an area (the image, or the whole productInfo/orderActions area) is coarser than
// selecting a specific zone within it — see SelectedZoneRef for that. Image area selection
// reuses ImageFocusContext instead (it predates this and already serves the same purpose);
// this context only ever holds a DynamicArea.
export interface SelectedAreaRef {
  mode: ConfigMode
  area: DynamicArea
}

export type SelectedAreaContextType = SelectedAreaRef | null

export const areaFor = (dropAreaType: Section['dropAreaType']): DynamicArea | null => {
  if (dropAreaType === 'product-info') return 'productInfo'
  if (dropAreaType === 'product-footer') return 'orderActions'
  return null
}

export interface ZoneStyleUpdate {
  direction?: FlexDirection | undefined
  justifyContent?: JustifyContent | undefined
  alignItems?: AlignItems | undefined
  wrap?: boolean | undefined
  gap?: string | undefined
  // Image-overlay sections only — see Section.hideUntilHover.
  hideUntilHover?: boolean | undefined
}

export type TileConfiguratorActionsContextType = {
  placeWidget: (mode: ConfigMode, sectionId: string, type: WidgetType, properties?: ComponentWidgetConfig) => void
  // sourceMode/targetMode are independent — a widget can be dragged from the grid canvas onto
  // the list canvas (or vice versa) as well as between two zones in the same canvas; a same-mode
  // move is just the sourceMode === targetMode case of the same logic. The moved widget keeps
  // its own id (widget ids are unique across both modes already — a single shared counter, not
  // one per mode, see createWidget) — only its `section` and `sequence` change.
  moveWidget: (sourceMode: ConfigMode, targetMode: ConfigMode, widgetId: string, targetSectionId: string) => void
  // Reorders within a single zone by trading the two widgets' sequence — the drag-to-swap
  // counterpart to the sidebar's removed numeric "Order" field. Dropping widget A directly onto
  // widget B swaps their positions;
  // dropping onto the zone's own background instead still goes through moveWidget/placeWidget
  // as before. Deliberately same-zone only (a no-op if the two widgets aren't in the same
  // section) — see tile-configurator-canvas.ts's handleWidgetDropOnWidget for how the two
  // drop targets are told apart.
  swapWidgets: (mode: ConfigMode, widgetIdA: string, widgetIdB: string) => void
  removeWidget: (mode: ConfigMode, widgetId: string) => void
  selectWidget: (ref: SelectedZoneRef) => void
  selectImage: (mode: ConfigMode) => void
  selectArea: (mode: ConfigMode, area: DynamicArea) => void
  clearSelection: () => void
  updateWidgetProperty: (mode: ConfigMode, widgetId: string, key: string, value: unknown) => void
  // An empty list removes the widget's conditions entirely (it always renders again).
  updateWidgetShowWhen: (mode: ConfigMode, widgetId: string, showWhen: WidgetConditionCfg[]) => void
  addRow: (mode: ConfigMode, area: DynamicArea) => void
  addColumn: (mode: ConfigMode, area: DynamicArea, rowIndex: number) => void
  removeZone: (mode: ConfigMode, area: DynamicArea, sectionId: string) => void
  // No `area` param — it identifies the section by id alone, so it's equally reusable for a
  // productInfo/orderActions zone or an above/below image-flow zone (there's no DynamicArea
  // for the latter). Never actually needed one; a prior version threaded an unused `_area`
  // through purely because every other zone/area action takes one.
  updateZoneStyle: (mode: ConfigMode, sectionId: string, partial: ZoneStyleUpdate) => void
  updateImageConfig: (partial: Partial<ImageConfigContextType>) => void
  // Both replace the *whole* per-area settings bucket with what's passed — same mechanic,
  // just called from different sidebar fields (padding vs. gap). Callers must read the
  // current full DynamicAreaSettings and spread+patch just their own key(s) (see
  // setPaddingValue/setGapValue in tile-configurator-zone-panel.ts), or they'll silently
  // wipe out whatever the other field last set. updateAreaPadding also covers above/below
  // (ImageFlowSlot) — updateAreaGap doesn't, since they have no row/column gap to set (see
  // ImageFlowSlot).
  updateAreaPadding: (mode: ConfigMode, area: DynamicArea | ImageFlowSlot, settings: DynamicAreaSettings) => void
  updateAreaGap: (mode: ConfigMode, area: DynamicArea, settings: DynamicAreaSettings) => void
  updateAreaAlignment: (mode: ConfigMode, area: DynamicArea, settings: DynamicAreaSettings) => void
  copyWidgets: (sourceMode: ConfigMode, targetMode: ConfigMode, options: WidgetCopyOptions) => number
  customizeMobileMode: (mode: MobileConfigMode) => void
  resetMobileMode: (mode: MobileConfigMode) => void
  loadFromCombinedConfig: (parsed: unknown) => void
  exportCombinedConfig: () => CombinedLayoutConfig
}

export const CanvasStateContext = createContext<CanvasStateContextType>(Symbol('csc-tile-configurator-canvas-state-context'))
export const MobileModeContext = createContext<MobileModeContextType>(Symbol('csc-tile-configurator-mobile-mode-context'))
export const CustomizedMobileModesContext = createContext<CustomizedMobileModesContextType>(Symbol('csc-tile-configurator-customized-mobile-modes-context'))
export const AreaLayoutContext = createContext<AreaLayoutContextType>(Symbol('csc-tile-configurator-area-layout-context'))
export const SelectedWidgetContext = createContext<SelectedWidgetContextType>(Symbol('csc-tile-configurator-selected-widget-context'))
export const SelectedAreaContext = createContext<SelectedAreaContextType>(Symbol('csc-tile-configurator-selected-area-context'))
export const ImageFocusContext = createContext<ImageFocusContextType>(Symbol('csc-tile-configurator-image-focus-context'))
export const ImageConfigContext = createContext<ImageConfigContextType>(Symbol('csc-tile-configurator-image-config-context'))
export const AreaPaddingContext = createContext<AreaPaddingContextType>(Symbol('csc-tile-configurator-area-padding-context'))
export const TileConfiguratorActionsContext = createContext<TileConfiguratorActionsContextType>(Symbol('csc-tile-configurator-actions-context'))
